import {useDeck} from "../../deck-management/context/DeckContext";
import {useEffect, useMemo, useRef, useState} from "react";
import {WordWithId, WordWithSound} from "../../../common/types/words";
import {useApiRequest} from "../../../common/hooks/useApiRequest";
import useTwoStepUpdate from "../../vocabulary/hooks/useTwoStepUpdate";
import {useAudio} from "../../../common/context/AudioContext";
import {SessionItem} from "../types/sessionTypes";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {ApiError} from "../../../common/types/requests";
import {getAudioForSource} from "../../../common/utils/audioUtils";


export interface UseLessonResult {
    currentWord: WordWithSound | null
    currentBatch: WordWithSound[] | null
    currentWordIndex: number
    remainingWordsInCurrentBatch: number
    allWordsInDeckFinished: boolean
    isLessonReady: boolean
    isReadyForQuiz: boolean
    startQuiz: () => void
    isQuizActive: boolean
    completedWords: WordWithSound[]
    next: () => void
    prev: () => void
    remainingQuizItems: SessionItem[]
    onItemPassedQuiz: (item : SessionItem) => void
    displayContinueLessonDialog: boolean
    nextBatch: () => void
    onCurrentLessonDeleted: () => void
    onCurrentWordUpdated: (updatedWord: WordWithId, updatedWordPromise: Promise<WordWithId>) => void
    playWordAudio: () => void
    playSentenceAudio: () => void
    cleanAudioFiles: () => void
    isLoadError: boolean
    isOutOfSyncError: boolean
}

interface LessonBatch {
    words: WordWithSound[]
    maxSeenIndex: number
    remainingWordsToReview: number
    ready: boolean
    isLastInDeck: boolean
}

function useLesson(batchSize: number) : UseLessonResult {

    const { currentDeck, ready : deckDataReady, isError: isDeckLoadError } = useDeck();
    const { play } = useAudio();

    const queryClient = useQueryClient();
    const allBatches = useRef<LessonBatch[]>([]);

    const [currentBatchIndex, setCurrentBatchIndex] = useState<number>(0);
    const [currentWordIndex, setCurrentWordIndex] = useState<number>(0);
    const [isQuizActive, setIsQuizActive] = useState<boolean>(false);
    const [completedWords, setCompletedWords] = useState<WordWithSound[]>([]);
    const [remainingQuizItems, setRemainingQuizItems]  = useState<SessionItem[]>([]);
    const [displayContinueLessonDialog, setDisplayContinueLessonDialog] = useState<boolean>(false);
    const [isLessonLoadError, setIsLessonLoadError] = useState<boolean>(false);
    const [isOutOfSyncError, setIsOutOfSyncError] = useState<boolean>(false);
    const [_stateVersion, setStateVersion] = useState<number>(0); // Used to force re-renders when necessary

    const {getRequest, postRequest} = useApiRequest();

    const currentBatch = useMemo(() => allBatches.current[currentBatchIndex] ?? null, [_stateVersion, currentBatchIndex]);

    const currentWord = useMemo(() => currentBatch?.ready ? currentBatch.words[currentWordIndex] : null, [currentBatch, currentWordIndex, _stateVersion]);

    const isLessonReady = useMemo(() => currentBatch?.ready, [currentBatch]);

    const isReadyForQuiz = useMemo(() => isLessonReady && !isQuizActive && currentBatch!.maxSeenIndex === currentBatch!.words.length - 1, [isLessonReady, isQuizActive, currentBatch, _stateVersion]);

    useEffect(() => {
        if (!deckDataReady || !currentDeck)
            return;
        prepareBatch(0).then(batch => {
            if (batch) {
                prepareBatch(1);
            }
        });
    }, [deckDataReady, currentDeck]);

    useEffect(() => {
        if (!isLessonReady)
            return;
        allBatches.current[currentBatchIndex].maxSeenIndex = Math.max(allBatches.current[currentBatchIndex].maxSeenIndex, currentWordIndex);
        setStateVersion(prev => prev + 1);
        if (currentBatchIndex > 0 && !currentBatch.isLastInDeck && !allBatches.current[currentBatchIndex + 1]?.ready)
            prepareBatch(currentBatchIndex + 1);
    }, [currentBatchIndex, isLessonReady]);

    const prepareBatch = async (batchIndex: number) : Promise<LessonBatch | null> => {
        try {
            const words = await fetchBatch(batchIndex);
            if (words.length === 0) {
                if (batchIndex > 0)
                    allBatches.current[batchIndex - 1].isLastInDeck = true;
                else
                    allBatches.current[batchIndex] = {words: [], maxSeenIndex: -1, remainingWordsToReview: 0, ready: true, isLastInDeck: true};
                setStateVersion(prev => prev + 1);
                return null;
            }
            const wordsWithSound = loadSounds(words);
            const newBatch = {words: wordsWithSound, maxSeenIndex: -1, remainingWordsToReview: wordsWithSound.length, ready: true, isLastInDeck: words.length < batchSize};
            allBatches.current[batchIndex] = newBatch;
            setStateVersion(prev => prev + 1);
            return newBatch;
        } catch (e) {
            return null;
        }

    }

    const fetchBatch = async (batchIndex: number) : Promise<WordWithId[]> => {
        setIsLessonLoadError(false);
        const searchParams = new URLSearchParams();
        searchParams.append("batch", String(batchSize));
        let startAfterWordId: string | null = null;
        if (batchIndex !== 0) {
            if (!allBatches.current[batchIndex - 1]?.ready)
                throw new Error("Previous batch not ready");
            const lastBatch = allBatches.current[batchIndex - 1].words;
            if (lastBatch.length === 0)
                throw new Error("No more words to load");
            startAfterWordId = lastBatch[lastBatch.length - 1].wordId;
            searchParams.append("start_after", startAfterWordId);
        }

        try {
            return await queryClient.fetchQuery({
                queryKey: ["lessons", currentDeck!.deckId, batchSize, batchIndex, startAfterWordId],
                queryFn: () => getRequest<WordWithId[]>(`decks/${currentDeck!.deckId}/lessons?${searchParams.toString()}`),
            });
        } catch (error) {
            setIsLessonLoadError(true);
            throw error;
        }
    }

    const loadSounds = (batch: WordWithId[]) : WordWithSound[] => {
        return batch.map(lesson => ({...lesson, sound: getAudioForSource(lesson.resources?.lemma)}));
    }

    useEffect(() => {
        if (!isLessonReady)
            return;
        allBatches.current[currentBatchIndex].maxSeenIndex = Math.max(allBatches.current[currentBatchIndex].maxSeenIndex, currentWordIndex);
        setStateVersion(prev => prev + 1);
    }, [currentWordIndex]);

    useEffect(() => {
        if (!isLessonReady)
            return;
        playCurrentWordAudio();
    }, [currentWord]);

    function playCurrentWordAudio() {
        if (!currentWord || !currentWord.sound) {
            return;
        }
        play(currentWord.sound);
    }

    function playSentenceAudio() {
        const audio = getAudioForSource(currentWord?.resources?.sentence);
        if (audio) {
            play(audio);
        }
    }

    const next = () => {
        if (currentWordIndex < currentBatch.words.length - 1) {
            setCurrentWordIndex(prev => prev + 1);
        }
    };

    const prev = () => {
        if (currentWordIndex > 0) {
            setCurrentWordIndex(prev => prev - 1);
        }
    };

    const startQuiz = () => {
        if (isReadyForQuiz) {
            const translationItems: SessionItem[] = currentBatch.words.map((word, idx) => ({
                seq: idx,
                type: "translation",
                status: "ready",
                prequelTo: idx + currentBatch.words.length,
                content: {wordId: word.wordId}
            }));
            const recollectionItems : SessionItem[] = currentBatch.words.map((word, idx) => ({
                seq: idx + currentBatch.words.length,
                type: "recollection",
                status: "ready",
                prequelTo: null,
                content: {wordId: word.wordId}
            }));
            setRemainingQuizItems(translationItems.sort(() => Math.random() - 0.5).concat(recollectionItems.sort(() => Math.random() - 0.5)));
            setIsQuizActive(true);
        }
    };

    const {mutate: submitLesson} = useMutation({
        mutationFn: (wordId: string) => postRequest(`decks/${currentDeck!.deckId}/lesson`, {wordId}),
        onError: (error: ApiError) => {
            if (error?.code === "WORD_ALREADY_LEARNED") {
                setIsOutOfSyncError(true);
            }
        }
    })

    const onItemPassedQuiz = (item : SessionItem) => {
        setRemainingQuizItems(prev => prev.filter(i => i.seq !== item.seq));

        if (!item.prequelTo) {
            const word = currentBatch.words.find(word => word.wordId === item.content.wordId);
            setCompletedWords(prev => prev.concat([word!]));
            allBatches.current[currentBatchIndex].remainingWordsToReview -= 1;
            if (allBatches.current[currentBatchIndex].remainingWordsToReview === 0)
                onQuizFinished();
            submitLesson(item.content.wordId);
        }
        setStateVersion(prev => prev + 1);
    };

    const onQuizFinished = () => {
        setIsQuizActive(false);
        setDisplayContinueLessonDialog(true);
    }

    const nextBatch = () => {
        setDisplayContinueLessonDialog(false);
        setCurrentWordIndex(0);
        if (!currentBatch.isLastInDeck)
            setCurrentBatchIndex(prev => prev + 1);
    }

    const onCurrentLessonDeleted = () => {
        allBatches.current[currentBatchIndex].words.splice(currentWordIndex, 1);
        allBatches.current[currentBatchIndex].remainingWordsToReview -= 1;
        setStateVersion(prev => prev + 1);
        if (allBatches.current[currentBatchIndex].words.length === 0) {
            nextBatch();
            return;
        }
        if (currentWordIndex >= allBatches.current[currentBatchIndex].words.length) {
            setCurrentWordIndex(allBatches.current[currentBatchIndex].words.length - 1);
        }
        allBatches.current[currentBatchIndex].maxSeenIndex = Math.min(allBatches.current[currentBatchIndex].maxSeenIndex, allBatches.current[currentBatchIndex].words.length - 1);
    }

    const {handleTwoStepUpdate} = useTwoStepUpdate<WordWithId>(
        () => allBatches.current[currentBatchIndex].words[currentWordIndex],
        (updatedWord, isNew) => {
            allBatches.current[currentBatchIndex].words[currentWordIndex] = isNew ? loadSounds([updatedWord!])[0] : updatedWord as WordWithSound;
        }
    );

    const onCurrentWordUpdated = (updatedWord: WordWithId, updatedWordPromise: Promise<WordWithId>) => {
        handleTwoStepUpdate(updatedWord, updatedWordPromise);
        setStateVersion(prev => prev + 1);
    };

    const cleanAudioFiles = () => {
        allBatches.current.forEach(batch => {
            batch.words.forEach(lesson => {
                lesson.sound = null;
            });
        });
        completedWords.forEach(lesson => lesson.sound = null);
    };

    return {
        currentWord,
        currentBatch: currentBatch?.words ?? null,
        currentWordIndex,
        remainingWordsInCurrentBatch: currentBatch?.remainingWordsToReview ?? -1,
        allWordsInDeckFinished: isLessonReady && currentBatch.isLastInDeck && currentBatch.remainingWordsToReview === 0,
        isLessonReady,
        isReadyForQuiz,
        startQuiz,
        isQuizActive,
        completedWords,
        next,
        prev,
        remainingQuizItems,
        onItemPassedQuiz,
        displayContinueLessonDialog,
        nextBatch,
        onCurrentLessonDeleted,
        onCurrentWordUpdated,
        playWordAudio: playCurrentWordAudio,
        playSentenceAudio,
        cleanAudioFiles,
        isLoadError: isDeckLoadError || isLessonLoadError,
        isOutOfSyncError
    }

}

export default useLesson;